import { useCallback, useEffect, useRef, useState } from 'react'

import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'

import { IViewComponent } from '@/core/app-builder/app/component'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import {
  formatColorAdaptiveValue,
  hasColorAdaptiveSettings,
  hasResponsiveSettings,
} from '@/shared/builder/viewport/responsive-settings'
import { debounce } from '@/shared/utils'

type NestedTypographySettings = Record<AppBreakpoint, string>

type ParentTypographySettings = Record<
  string,
  NestedTypographySettings & {
    overflowSetting?: NestedTypographySettings
    detailPageSetting?: NestedTypographySettings
  }
>

type ActiveThemeMode = 'light' | 'dark'

function parseResponsiveSettingValue(
  key: string,
  settingValue: any,
  device: AppBreakpoint,
  isOverlay: boolean = false,
  isDetailPage: boolean = false
) {
  // Return primitive values as-is
  if (typeof settingValue !== 'object' || !hasResponsiveSettings(key)) {
    return settingValue
  }

  // Handle overlay context - use overflowSetting if available
  if (isOverlay && settingValue?.overflowSetting) {
    return settingValue.overflowSetting[device] || settingValue[device]
  }

  // Handle detail page context - use detailPageSetting if available
  if (isDetailPage && settingValue?.detailPageSetting) {
    return settingValue.detailPageSetting[device] || settingValue[device]
  }

  // Handle typography settings
  if (key.toLowerCase().includes('-typography')) {
    const nestedSettings = settingValue as ParentTypographySettings

    // Check if it's a typography group (not individual typography setting)
    const isTypographyGroup = !nestedSettings[device]
    if (isTypographyGroup) {
      const parsedSettings: Record<string, string> = {}

      for (const nestedKey in nestedSettings) {
        const breakpointValues =
          isOverlay && nestedSettings?.[nestedKey]?.overflowSetting
            ? nestedSettings?.[nestedKey]?.overflowSetting
            : isDetailPage && nestedSettings?.[nestedKey]?.detailPageSetting
              ? nestedSettings?.[nestedKey]?.detailPageSetting
              : nestedSettings?.[nestedKey]
        parsedSettings[nestedKey] = breakpointValues?.[device] ?? ''
      }

      return parsedSettings
    }
  }

  // Handle standard responsive settings (device-based)
  return settingValue[device]
}

/**
 * React Hook for builder component to access its (reactive) settings data.
 *
 */
export const useCompSettings = (component?: IViewComponent) => {
  const settingsRef = useRef<Record<string, any>>({})

  const { activeLayer } = useActiveLayerContext()
  const { device } = useBreakpoint()
  const [scheme] = useBuilderColorSchemeV2()
  const mode = scheme.current as ActiveThemeMode

  const isOverlay = activeLayer?.type === 'overlay' || false
  const isDetailPage = (!!activeLayer?.tableId && activeLayer?.type === 'page') || false

  // Parse setting values with safeguards
  const parseSettingValues = useCallback(
    (settings = {}) => {
      if (!settings || typeof settings !== 'object') {
        return settingsRef.current // Return previous valid settings if new ones are invalid
      }

      return Object.entries(settings).reduce((acc: any, [key, val]) => {
        // Add null check for val
        if (val === undefined || val === null) {
          acc[key] = settingsRef.current[key] // Preserve previous value if exists
          return acc
        }

        if (hasColorAdaptiveSettings(key)) {
          acc[key] = formatColorAdaptiveValue(key, val, val[mode], mode)[mode]
          return acc
        }

        if (hasResponsiveSettings(key)) {
          acc[key] = parseResponsiveSettingValue(key, val, device, isOverlay, isDetailPage)
          return acc
        }

        acc[key] = val
        return acc
      }, {})
    },
    [device, mode, isOverlay, isDetailPage]
  )

  const [settings, setSettings] = useState<Record<string, any>>(() => {
    const initialSettings = parseSettingValues(component?.settings)
    settingsRef.current = initialSettings // Store initial settings in ref
    return initialSettings
  })

  // Update settings when component changes
  useEffect(() => {
    if (!component) return

    const newSettings = parseSettingValues(component.settings)
    if (Object.keys(newSettings).length > 0) {
      // Only update if we have valid settings
      settingsRef.current = newSettings
      setSettings(newSettings)
    }
  }, [component, parseSettingValues])

  // Handle settings updates
  useEffect(() => {
    if (!component) return

    const debounceUpdateAllSettings = debounce(() => {
      const newSettings = parseSettingValues(component.settings)
      if (Object.keys(newSettings).length > 0) {
        settingsRef.current = newSettings
        setSettings(newSettings)
      }
    }, 50)

    const updateSetting = async (key: string) => {
      if (!component.settings) return
      let value = component.settings[key]

      if (hasColorAdaptiveSettings(key)) {
        value = formatColorAdaptiveValue(key, value, value[mode], mode)[mode]
      }

      if (hasResponsiveSettings(key)) {
        value = parseResponsiveSettingValue(key, value, device)
      }

      setSettings((prev) => {
        const updated = { ...prev, [key]: value }
        settingsRef.current = updated
        return updated
      })
    }

    const unsubscribe = component.subscribe(async (key: string) => {
      if (key === 'all') {
        debounceUpdateAllSettings()
      } else {
        updateSetting(key)
      }
    })

    return () => {
      unsubscribe()
    }
  }, [component, device, mode, parseSettingValues])

  // Return settings with fallback to ref
  // console.log(Object.keys(settings).length > 0 ? settings : settingsRef.current)
  return Object.keys(settings).length > 0 ? settings : settingsRef.current
}
